import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["tour", "tourBody", "prevButton", "nextButton"];
  currentIndex = 0;
  tourSteps = [];
  highlightClass = "relative";
  highlightContainer = `<div class="absolute w-[calc(100%+20px)] h-[calc(100%+10px)] 
                          -top-[5px] -left-[10px] z-[-1] 
                          shadow-lg border-2 border-primary 
                          rounded-md bg-neutrality">
                        </div>`;
  activeTourId = null;

  connect() {
    this.collectTourSteps();
  }

  collectTourSteps() {
    const allSteps = document.querySelectorAll("[data-step]");
    this.tourSteps = Array.from(allSteps).sort((a, b) => {
      return this.extractStepNumber(a) - this.extractStepNumber(b);
    });
  }

  extractStepNumber(element) {
    return parseInt(element.dataset.step, 10);
  }

  openTour(event) {
    const trigger = event.currentTarget;
    this.activeTourId = trigger.dataset.tourId;

    this.tourSteps = Array.from(document.querySelectorAll(`[data-tour-id="${this.activeTourId}"]`))
      .filter(el => el.dataset.step)
      .sort((a, b) => this.extractStepNumber(a) - this.extractStepNumber(b));

    if (this.tourSteps.length === 0) {
      console.warn(`Keine gültigen Tour-Schritte gefunden für Tour-ID: ${this.activeTourId}`);
      return;
    }

    this.currentIndex = this.tourSteps.findIndex(el => el === trigger);
    if (this.currentIndex === -1 || this.tourSteps[this.currentIndex].dataset.skip === "true") {
      this.currentIndex = this.findNextValidStep(0);
    }

    this.showTourStep(this.currentIndex);
  }

  async showTourStep(index) {
    this.addOverlay();
    this.disableScroll();

    if (index < 0 || index >= this.tourSteps.length) return;

    const trigger = this.tourSteps[index];

    if (trigger.dataset.skip === "true") {
      this.nextStep();
      return;
    }

    const content = trigger.dataset.content;
    if (!content) return;

    this.highlightTrigger(trigger);
    this.tourBodyTarget.innerHTML = content;
    this.tourTarget.classList.remove("hidden");


    requestAnimationFrame(() => {
      this.tourTarget.classList.add("opacity-100", "scale-100");
      this.tourTarget.classList.remove("opacity-0", "scale-95");
    });
 
    this.updateNavButtons();
    await this.scrollToElement(trigger);
    this.positionModal(trigger);
  }

  closeTour(event) {
    if (event && event.target.closest("[data-tour-target='tourBody']")) return;

    this.tourTarget.classList.add("opacity-0", "scale-95");
    this.tourTarget.classList.remove("opacity-100", "scale-100");

    this.enableScroll();

    this.removeHighlight();

    this.tourTarget.addEventListener("transitionend", () => {
      this.tourTarget.classList.add("hidden");
    }, { once: true });

    this.activeTourId = null;
    this.removeOverlay();

  }

  addOverlay() {
    if (document.getElementById("tour-overlay")) return; // Prevent multiple overlays

    const overlay = document.createElement("div");
    overlay.id = "tour-overlay";
    overlay.style.position = "fixed";
    overlay.style.top = "0";
    overlay.style.left = "0";
    overlay.style.width = "100vw";
    overlay.style.height = "100vh";
    // overlay.style.background = "rgba(0, 0, 0, 0.3)"; //
    overlay.style.zIndex = "999";
    overlay.style.cursor = "pointer";

    overlay.addEventListener("click", (event) => this.closeTour(event));

    document.body.appendChild(overlay);
  }

  removeOverlay() {
    const overlay = document.getElementById("tour-overlay");
    if (overlay) overlay.remove();
  }

  async scrollToElement(element) {
      this.enableScroll();
    
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const isVisible = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );

      if (!isVisible) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    this.disableScroll();
  }

  prevStep() {
    let newIndex = this.currentIndex - 1;
    while (newIndex >= 0 && this.tourSteps[newIndex].dataset.skip === "true") {
      newIndex -= 1;
    }

    if (newIndex >= 0) {
      this.currentIndex = newIndex;
      this.showTourStep(this.currentIndex);
    }

    this.updateNavButtons();
  }

  nextStep() {
    let newIndex = this.currentIndex + 1;
    while (newIndex < this.tourSteps.length && this.tourSteps[newIndex].dataset.skip === "true") {
      newIndex += 1;
    }

    if (newIndex < this.tourSteps.length) {
      this.currentIndex = newIndex;
      this.showTourStep(this.currentIndex);
    }

    this.updateNavButtons();
  }

  updateNavButtons() {
    if (this.hasPrevButtonTarget) {
      const hasPrevStep = this.findPrevValidStep(this.currentIndex) !== null;
      this.prevButtonTarget.classList.toggle("hidden", !hasPrevStep);
    }
    if (this.hasNextButtonTarget) {
      const hasNextStep = this.findNextValidStep(this.currentIndex) !== null;
      this.nextButtonTarget.classList.toggle("hidden", !hasNextStep);
    }
  }

  findNextValidStep(index) {
    let newIndex = index + 1;
    while (newIndex < this.tourSteps.length && this.tourSteps[newIndex].dataset.skip === "true") {
      newIndex += 1;
    }
    return newIndex < this.tourSteps.length ? newIndex : null;
  }

  findPrevValidStep(index) {
    let newIndex = index - 1;
    while (newIndex >= 0 && this.tourSteps[newIndex].dataset.skip === "true") {
      newIndex -= 1;
    }
    return newIndex >= 0 ? newIndex : null;
  }

  positionModal(trigger) {
    const triggerRect = trigger.getBoundingClientRect();
    const modal = this.tourTarget;
    const modalWidth = modal.offsetWidth;
    const modalHeight = modal.offsetHeight;
    const viewportWidth = window.innerWidth;

    let left, top;

    top = triggerRect.top - modalHeight - 10 + window.scrollY;

    if (top < window.scrollY) {
      top = window.scrollY + 10;
    }

    if (triggerRect.right + modalWidth + 10 < viewportWidth) {
      left = triggerRect.right + 20 + window.scrollX;
    } else if (triggerRect.left - modalWidth - 20 > 0) {
      left = triggerRect.left - modalWidth - 20 + window.scrollX;
    } else {
      left = Math.max(20, (viewportWidth - modalWidth) / 2 + window.scrollX);
    }

    left = Math.max(20, Math.min(left, viewportWidth - modalWidth - 20));

    modal.style.position = "absolute";
    modal.style.left = `${left}px`;
    modal.style.top = `${top}px`;
  }

  highlightTrigger(trigger) {
    this.removeHighlight();
    if (!trigger) return;
    trigger.classList.add(...this.highlightClass.split(" "));

    const highlightWrapper = document.createElement("div");
    highlightWrapper.innerHTML = this.highlightContainer.trim();

    const highlightElement = highlightWrapper.firstElementChild;
    if (!highlightElement) return;

    highlightElement.dataset.highlight = "true";
    trigger.prepend(highlightElement);
  }

  removeHighlight() {
    document.querySelectorAll("[data-highlight]").forEach(el => el.remove());
    this.tourSteps.forEach(trigger => {
      trigger.classList.remove(...this.highlightClass.split(" "));
    });
  }

  disableScroll() {
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
  }

  enableScroll() {
    document.body.style.overflow = "";
    document.body.style.height = "";
  }
}